/* eslint-disable react/jsx-props-no-spreading */
import { Layout } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import memoizeOne from 'memoize-one';
import { pathToRegexp } from 'path-to-regexp';
import React from 'react';
import { ContainerQuery } from 'react-container-query';
import { Drawer, Modal } from '@arcflight/tf-component-library';
import DocumentTitle from 'react-document-title';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import disableAutoCorrect from '../utils/disableAutoCorrect';
import logo from '../assets/logo.svg';
import logoText from '../assets/logoText.svg';
import SiderMenu from '../components/SiderMenu';
import GlobalModalContent from '../components/GlobalModalContent/GlobalModalContent';
import { changeDrawerMode, changeDrawerVisibility, changeModalVisibility } from '../models/drawer';
import menuDataGenerator from '../utils/menuData';
import Banner from '../components/Banner/Banner';
import ToastBuilder from '../components/TFToasts/ToastBuilder';
import { fetch } from '../models/userSettings/actions';
import { changeBannerVisible, changeLayoutCollapsed } from '../models/menu';
import Footer from './Footer';
import MyHeader from './Header';
import styles from './BasicLayout.module.less';
import { UsersnapProvider } from './UserSnapContext';

const query = {
  'screen-xs': {
    maxWidth: 575,
  },
  'screen-sm': {
    minWidth: 576,
    maxWidth: 767,
  },
  'screen-md': {
    minWidth: 768,
    maxWidth: 991,
  },
  'screen-lg': {
    minWidth: 992,
    maxWidth: 1199,
  },
  'screen-xl': {
    minWidth: 1200,
    maxWidth: 1599,
  },
  'screen-xxl': {
    minWidth: 1600,
  },
};

const mediaQueryList = window.matchMedia('(max-width: 767px)');

class BasicLayout extends React.PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    userSettings: PropTypes.object,
    collapsed: PropTypes.bool.isRequired,
    bannerVisible: PropTypes.bool.isRequired,
    drawer: PropTypes.object.isRequired,
    menu: PropTypes.object,
    fixSiderbar: PropTypes.bool.isRequired,
    fixedHeader: PropTypes.bool.isRequired,
    layout: PropTypes.string.isRequired,
    navTheme: PropTypes.string.isRequired,
    children: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    aircraftMap: PropTypes.instanceOf(Map).isRequired,
  };

  static defaultProps = {
    menu: undefined,
    userSettings: {
      details: {
        survey_submitted_date: '',
      },
    },
  };

  state = {
    isMobile: false,
    menuData: menuDataGenerator(
      this.props.intl.formatMessage,
      this.props.userSettings?.details?.dashboard_permissions,
      this.props.userSettings?.details?.people,
      this.props.userSettings?.details?.operators?.reduce((flags, op) => {
        if (op.feature_flags) {
          op.feature_flags.forEach((flag) => {
            if (!flags.includes(flag.feature_name)) {
              flags.push(flag.feature_name);
            }
          });
        }
        return flags;
      }, []),
      this.props.userSettings.details?.operators?.reduce((modules, op) => {
        if (op.enabled_tech_log_modules) {
          op.enabled_tech_log_modules.forEach((module) => {
            if (!modules.includes(module.module_name.toLowerCase())) {
              modules.push(module.module_name.toLowerCase());
            }
          });
        }
        return modules;
      }, []),
      this.props.userSettings?.details?.operators,
    ),

    completeDate: null,
    dismissDate: null,
    userDetails: null,
  };

  constructor(props) {
    super(props);
    this.getPageTitle = memoizeOne(this.getPageTitle);
    this.matchParamsPath = memoizeOne(this.matchParamsPath, isEqual);
  }

  componentDidMount() {
    const { dispatch, userSettings } = this.props;
    const { isMobile } = this.state;
    if (!userSettings || !userSettings.details) {
      dispatch(fetch());
    }
    if (userSettings && userSettings.details) {
      this.checkIfOverdueOrSuspended();
    }
    // Dispatching resize event to prevent DASH-1096 bug
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 10);
    window.addEventListener(
      'orientationchange',
      () => {
        if (isMobile) this.handleMenuCollapse(true);
      },
      false,
    );
    this.updateDates();
    disableAutoCorrect();
  }

  componentDidUpdate(prevProps, prevState) {
    // After changing to phone mode,
    // if collapsed is true, you need to click twice to display
    const { isMobile } = this.state;
    const { collapsed, menu, userSettings } = this.props;
    if (isMobile && !prevState.isMobile && !collapsed) {
      this.handleMenuCollapse(false);
    }
    if (
      menu !== prevProps.menu ||
      !_.isEqual(prevProps.userSettings && prevProps.userSettings.details, userSettings.details)
    ) {
      this.setMenuData();
    }
    disableAutoCorrect();
  }

  setMenuData() {
    const {
      userSettings,
      intl: { formatMessage },
    } = this.props;
    this.setState({
      menuData: menuDataGenerator(
        formatMessage,
        userSettings?.details?.dashboard_permissions,
        userSettings?.details?.people,
        userSettings?.details?.operators?.reduce((flags, op) => {
          if (op.feature_flags) {
            op.feature_flags.forEach((flag) => {
              if (!flags.includes(flag.feature_name)) {
                flags.push(flag.feature_name);
              }
            });
          }
          return flags;
        }, []),
        userSettings.details?.operators?.reduce((modules, op) => {
          if (op.enabled_tech_log_modules) {
            op.enabled_tech_log_modules.forEach((module) => {
              if (!modules.includes(module.module_name.toLowerCase())) {
                modules.push(module.module_name.toLowerCase());
              }
            });
          }
          return modules;
        }, []),
        userSettings?.details?.operators,
      ),
    });
  }

  updateDates = () => {
    const {
      userSettings: { details },
    } = this.props;

    const { userDetails } = this.state;

    let operators;
    if (details?.operators) operators = details.operators;
    if (details && !userDetails) {
      this.setState({
        userDetails: {
          email: details?.email,
          userId: details?.id,
          name: `${details?.first_name} ${details?.last_name}`,
        },
      });
    }
    if (operators) {
      const opSettings = operators[0].operator_setting;
      if (opSettings) {
        this.setState({
          completeDate: opSettings.survey_complete_reshow_days,
          dismissDate: opSettings.survey_dismissed_reshow_days,
        });
      }
    }
  };

  updateMediaScreenSize = () => {
    this.setState({ isMobile: mediaQueryList.matches });
  };

  getPageTitle = (pathname) => {
    const {
      intl: { messages },
      match: {
        params: { id },
      },
      userSettings,
      aircraftMap,
    } = this.props;

    const currentAircraft = aircraftMap.get(id);
    let intermittentFaultTitle = 'Intermittent Faults';
    let dentAndBuckleTitle = 'Dent & Buckle';
    if (currentAircraft) {
      const currentOrg = userSettings?.details?.operators?.find((op) => op.id === currentAircraft?.operator_id);
      intermittentFaultTitle = currentOrg?.operator_setting?.intermittent_fault_name_plural;
      dentAndBuckleTitle = currentOrg?.operator_setting?.dent_and_buckle;
    }

    const intlPath = pathname
      .split('/')
      .filter((path) => path !== id && path.length > 0)
      .map((path) => path.charAt(0).toUpperCase() + path.slice(1))
      .join('.');
    let message = messages[`menu.${intlPath}`];
    if (intlPath === 'Aircraft.Damage_maps.List_view') {
      message = dentAndBuckleTitle;
    }
    if (intlPath === 'Aircraft.Damage_maps.Schematic_view') {
      message = dentAndBuckleTitle;
    }
    if (intlPath === 'Aircraft.Dent_and_buckle') {
      message = dentAndBuckleTitle;
    }
    if (intlPath === 'Aircraft.Intermittent_faults') {
      message = intermittentFaultTitle;
    }

    return message ? `${message} - TrustFlight` : 'TrustFlight';
  };

  getLayoutStyle = () => {
    const { isMobile } = this.state;
    const { fixSiderbar, collapsed, layout } = this.props;
    if (fixSiderbar && layout !== 'topmenu' && !isMobile) {
      return {
        paddingLeft: collapsed ? '80px' : '200px',
      };
    }
    return null;
  };

  getContentStyle = () => {
    const { fixedHeader } = this.props;
    return {
      margin: '24px 24px 0',
      paddingTop: fixedHeader ? 64 : 0,
    };
  };

  matchParamsPath = (pathname) => {
    const pathKey = Object.keys(this.breadcrumbNameMap).find((key) => pathToRegexp(key).test(pathname));
    return this.breadcrumbNameMap[pathKey];
  };

  handleMenuCollapse = (collapsed, fromButton) => {
    if (fromButton !== 'responsive' || window.innerWidth < 1100) {
      const { dispatch } = this.props;
      dispatch(
        changeLayoutCollapsed({
          payload: collapsed,
        }),
      );
    }
  };

  checkIfOverdueOrSuspended() {
    const {
      userSettings: {
        details: { operators },
      },
      dispatch,
    } = this.props;
    const areAnyOperatorsOverdueOrSuspended = operators?.some(
      (operator) => operator?.billing_status === 'overdue' || operator?.billing_status === 'suspended',
    );
    if (areAnyOperatorsOverdueOrSuspended) {
      dispatch(changeBannerVisible({ payload: true }));
    }
  }

  render() {
    const {
      navTheme,
      children,
      location: { pathname },
      drawer,
      dispatch,
      bannerVisible,
      userSettings: { details },
    } = this.props;
    const { menuData, completeDate, dismissDate, userDetails } = this.state;
    const isMobile = mediaQueryList.matches;

    let surveySubmittedDate;
    if (details) {
      surveySubmittedDate = details?.survey_submitted_date;
    }
    document.addEventListener('wheel', () => {
      if (document.activeElement instanceof HTMLInputElement && document.activeElement.type === 'number') {
        document.activeElement.blur();
      }
    });

    return (
      <>
        <DocumentTitle title={this.getPageTitle(pathname)}>
          {completeDate && dismissDate && (
            <UsersnapProvider
              submitDate={surveySubmittedDate}
              completeDate={completeDate}
              dismissDate={dismissDate}
              userDetails={userDetails}
            >
              <ContainerQuery query={query}>
                {(params) => (
                  <div id="layoutWrapper" className={classNames(params)}>
                    <Banner />
                    <Drawer
                      visible={drawer?.visible}
                      data={drawer?.drawerContent ? drawer?.drawerContent : undefined}
                      bannerVisible={bannerVisible}
                      handleClose={() => {
                        if (drawer?.drawerChanges && (drawer?.mode === 'edit' || drawer?.mode === 'add')) {
                          dispatch(changeModalVisibility({ payload: true }));
                        } else {
                          dispatch(changeDrawerVisibility({ payload: false }));
                          dispatch(changeDrawerMode({ payload: 'view' }));
                        }
                      }}
                    >
                      {drawer?.drawerContent}
                    </Drawer>
                    <Modal
                      width={420}
                      isOpen={drawer?.changeModalVisible}
                      handleClose={() => dispatch(changeModalVisibility(false))}
                    >
                      <GlobalModalContent />
                    </Modal>
                    <ToastBuilder />
                    <Layout>
                      {isMobile ? null : (
                        <SiderMenu
                          logo={logo}
                          logoText={logoText}
                          theme={navTheme}
                          onCollapse={this.handleMenuCollapse}
                          menuData={menuData}
                          isMobile={isMobile}
                          {...this.props}
                        />
                      )}
                      <Layout
                        style={{
                          ...this.getLayoutStyle(),
                        }}
                        className={styles.contentLayout}
                      >
                        {isMobile ? (
                          <MyHeader
                            menuData={menuData}
                            handleMenuCollapse={this.handleMenuCollapse}
                            logo={logo}
                            isMobile={isMobile}
                            {...this.props}
                          />
                        ) : null}

                        {children}
                        <Footer />
                      </Layout>
                    </Layout>
                  </div>
                )}
              </ContainerQuery>
            </UsersnapProvider>
          )}
        </DocumentTitle>
      </>
    );
  }
}

export default injectIntl(
  withRouter(
    connect(({ menu, userSettings, drawer, aircraft }) => ({
      collapsed: menu.collapsed,
      bannerVisible: menu.bannerVisible,
      userSettings,
      aircraftMap: aircraft.aircraftMap,
      drawer,
      ...userSettings.ui,
    }))(BasicLayout),
  ),
);
